import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getFirestore, collection, getDocs, query, orderBy } from 'firebase/firestore';
import {
  History, Loader2, AlertTriangle, RefreshCw, Download, ChevronRight, ChevronDown, Search,
  BadgeCheck, Clock, Send, Hammer, ShieldCheck, FileText,
} from 'lucide-react';
import CpscTable, { CPSC_TH_CLASS } from './CpscTable';
import { escCsv } from '../../../shared/utils/csvSafe'; // CHA-193 — shared formula-injection-safe CSV escaper

/**
 * CpscAuditHistory — CHA-174 (final ticket of the CPSC eFiling epic). Read-only audit / history view under
 * Imports → Audit & History. NO writes — this is a compliance/operational read surface.
 *
 * Stage-1 verified (§6.2.1): every CPSC lifecycle event (assemble → confirm → submit → certify → broker
 * handoff) lands in ONE per-draft subcollection `/cpsc_drafts/{tenantId}/drafts/{draftId}/audit/{eventId}`
 * (broker handoff too — NOT a separate /broker_handoffs/). There is NO cpsc_submissions collection; the
 * draft record itself carries the summary row. So this is fully client-side, path-scoped (tenant-safe):
 *   - Table (landing): read `/cpsc_drafts/{tenantId}/drafts` — one row per draft (decision 4).
 *   - Timeline (drill-down): read that draft's `/audit` subcollection on expand.
 * No server callable, no composite index, no rules change (decision-4 read pattern). A FLAT cross-draft
 * event log would need a collectionGroup('audit') query that can't be tenant-isolated (audit docs carry no
 * tenantId) without a write-side change — deferred follow-on, out of CHA-174's read-only scope.
 */
const s = (v) => String(v ?? '').trim();

// Firestore Timestamp → millis / display (guard nulls — a serverTimestamp can be momentarily pending).
const toMillis = (ts) => (ts && typeof ts.toMillis === 'function' ? ts.toMillis() : 0);
const fmt = (ts) => (ts && typeof ts.toDate === 'function' ? ts.toDate().toLocaleString() : '—');

// Lifecycle status → pill (parity with CpscDrafts). All states appear here (unlike Broker Handoff).
const STATUS = {
  error: { tone: 'red', label: 'Error' }, needs_confirmation: { tone: 'amber', label: 'Needs confirmation' },
  draft: { tone: 'slate', label: 'Draft' }, confirmed: { tone: 'blue', label: 'Confirmed' },
  ready: { tone: 'green', label: 'Ready' }, submitted_validation: { tone: 'blue', label: 'Submitting' },
  awaiting_certification: { tone: 'green', label: 'Awaiting certification' }, submission_error: { tone: 'red', label: 'Submission error' },
  certifying: { tone: 'blue', label: 'Certifying' }, certified: { tone: 'green', label: 'Certified' },
  certification_failed: { tone: 'red', label: 'Certification failed' }, certification_pending: { tone: 'amber', label: 'Pending at CPSC' },
  superseded: { tone: 'slate', label: 'Superseded' }, archived: { tone: 'slate', label: 'Archived' },
};
const TONE = {
  green: 'text-carbon-green-50 border-carbon-green-50 bg-carbon-green-10', amber: 'text-carbon-orange-70 border-carbon-orange-40 bg-carbon-white',
  red: 'text-carbon-red-60 border-carbon-red-60 bg-carbon-white', blue: 'text-carbon-blue-60 border-carbon-blue-60 bg-carbon-blue-10',
  slate: 'text-carbon-gray-70 border-carbon-gray-30 bg-carbon-white',
};
// Audit action → icon + human label (for the timeline).
const ACTION = {
  assemble: { icon: Hammer, label: 'Assembled' }, reassemble: { icon: Hammer, label: 'Re-assembled' },
  confirm_field: { icon: BadgeCheck, label: 'Field confirmed' }, mark_confirmed: { icon: BadgeCheck, label: 'Marked confirmed' },
  mark_ready: { icon: ShieldCheck, label: 'Marked ready' }, reopen: { icon: RefreshCw, label: 'Reopened' },
  delete: { icon: AlertTriangle, label: 'Deleted' }, supersede: { icon: RefreshCw, label: 'Superseded' },
  submit: { icon: Send, label: 'Submitted to CPSC' }, validation_passed: { icon: BadgeCheck, label: 'Validated (awaiting cert)' },
  validation_failed: { icon: AlertTriangle, label: 'Validation failed' },
  certify: { icon: BadgeCheck, label: 'Certify started' }, retry_certify: { icon: RefreshCw, label: 'Retry certify' },
  certification_passed: { icon: BadgeCheck, label: 'Certified' }, certification_failed: { icon: AlertTriangle, label: 'Certification failed' },
  certification_pending: { icon: Clock, label: 'Certification pending' }, broker_handoff_email: { icon: FileText, label: 'Broker handoff emailed' },
};

const DATE_RANGES = [{ label: 'Last 30 days', days: 30 }, { label: 'Last 90 days', days: 90 }, { label: 'Last year', days: 365 }, { label: 'All time', days: 0 }];

const CSV_HEADERS =['Report No', 'Style No', 'Version ID', 'Status', 'Last activity', 'Submitted by', 'Certified by', 'Draft ID'];
const summaryRow = (d) => [s(d.reportNo), s(d.styleNo), s(d.versionId), (STATUS[d.status] || {}).label || s(d.status), fmt(d.updatedAt), s(d.submittedBy), s(d.certifiedBy), s(d.draftId)];

export default function CpscAuditHistory({ userProfile } = {}) {
  // Same gate as the sibling CPSC tabs (admin | platform_owner).
  const role = userProfile?.role;
  const isPlatformRole = role === 'platform_owner';
  const isAdmin = isPlatformRole || role === 'admin';
  const tenantId = userProfile?.tenantId || 'inmocean';

  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);           // draftId whose timeline is open
  const [auditByDraft, setAuditByDraft] = useState({});     // draftId → { loading, events, error }
  const [search, setSearch] = useState('');
  const [rangeDays, setRangeDays] = useState(30);           // decision 5 default

  const loadSeq = useRef(0); // generation token — a newer load()/tenant-switch invalidates in-flight reads

  const load = useCallback(async () => {
    const seq = (loadSeq.current += 1);
    // Clear tenant-scoped state first (no stale cross-tenant data on switch/failed reload — CHA-173 lesson).
    setLoading(true); setError(null); setDrafts([]); setExpanded(null); setAuditByDraft({});
    try {
      const db = getFirestore();
      const snap = await getDocs(collection(db, 'cpsc_drafts', tenantId, 'drafts'));
      if (seq !== loadSeq.current) return; // a newer load superseded this — drop stale results (CodeRabbit)
      setDrafts(snap.docs.map((d) => ({ draftId: d.id, ...d.data() })));
    } catch (e) {
      if (seq !== loadSeq.current) return;
      console.error('Failed to load CPSC audit history', e); // don't swallow (§8.3)
      setError('Could not load history. Check access and retry.');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  // Drill-down: load one draft's /audit subcollection on first expand (path-scoped ⇒ tenant-safe).
  const openTimeline = useCallback(async (draftId) => {
    if (expanded === draftId) { setExpanded(null); return; }
    setExpanded(draftId);
    if (auditByDraft[draftId]?.events) return; // only skip a SUCCESSFUL load — an errored/loading entry re-fetches on re-expand
    const seq = loadSeq.current; // if a reload/tenant-switch lands mid-fetch, drop this stale timeline
    setAuditByDraft((p) => ({ ...p, [draftId]: { loading: true } }));
    try {
      const db = getFirestore();
      const snap = await getDocs(query(collection(db, 'cpsc_drafts', tenantId, 'drafts', draftId, 'audit'), orderBy('at', 'asc')));
      if (seq !== loadSeq.current) return;
      setAuditByDraft((p) => ({ ...p, [draftId]: { events: snap.docs.map((e) => ({ id: e.id, ...e.data() })) } }));
    } catch (e) {
      if (seq !== loadSeq.current) return;
      console.error('Failed to load audit timeline', draftId, e);
      setAuditByDraft((p) => ({ ...p, [draftId]: { error: 'Could not load the timeline.' } }));
    }
  }, [expanded, auditByDraft, tenantId]);

  // Filter: date range on updatedAt (decision — ratified `updatedAt`) + free-text across Report/Style/draftId.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cutoff = rangeDays ? Date.now() - rangeDays * 86400000 : 0;
    return drafts
      .filter((d) => !cutoff || toMillis(d.updatedAt) >= cutoff)
      .filter((d) => !q || [d.reportNo, d.styleNo, d.draftId].some((f) => s(f).toLowerCase().includes(q)))
      .sort((a, b) => toMillis(b.updatedAt) - toMillis(a.updatedAt)); // most recent activity first
  }, [drafts, search, rangeDays]);

  if (!isAdmin) {
    return (
      <div className="font-plex text-sm text-carbon-gray-70 bg-carbon-gray-10 border border-carbon-border px-4 py-6 text-center">
        CPSC Audit &amp; History is restricted to administrators.
      </div>
    );
  }

  const exportCsv = () => {
    if (!visible.length) { setError('Nothing to export in this range.'); return; }
    const csv = [CSV_HEADERS, ...visible.map(summaryRow)].map((r) => r.map(escCsv).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `cpsc-audit-history_${tenantId}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="font-plex text-carbon-ink space-y-4 max-w-4xl">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-carbon-ink">CPSC eFiling — Audit &amp; History</h3>
          <p className="text-sm text-carbon-gray-70">
            Read-only history of every CPSC draft, submission, certification, and broker handoff. Click a row
            to see its full timeline. For corrections, create a new draft — records are immutable.
          </p>
        </div>
        <button onClick={load} disabled={loading} className="flex items-center gap-1 px-2 py-1 text-xs border border-carbon-gray-30 text-carbon-gray-70 hover:bg-carbon-gray-10 disabled:opacity-50">
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Refresh
        </button>
      </div>

      {isPlatformRole && (
        <div className="text-xs text-carbon-blue-70 bg-carbon-blue-10 border border-carbon-blue-60 px-3 py-2">
          Acting as <strong>{role}</strong> against tenant: <strong>{tenantId}</strong>
        </div>
      )}
      {error && (
        <div className="text-sm text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-3 py-2 flex items-start gap-1">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-carbon-gray-40" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Report No / Style No / draft ID"
            className="text-xs border border-carbon-gray-30 bg-carbon-gray-10 pl-7 pr-2 py-1.5 w-64 focus:outline-none focus:ring-1 focus:ring-carbon-blue-60" aria-label="search history"
          />
        </div>
        <select value={rangeDays} onChange={(e) => setRangeDays(Number(e.target.value))} className="text-xs border border-carbon-gray-30 bg-carbon-white px-2 py-1.5 text-carbon-gray-80" aria-label="date range">
          {DATE_RANGES.map((r) => <option key={r.days} value={r.days}>{r.label}</option>)}
        </select>
        <div className="flex-1" />
        <span className="font-plex-mono text-[10.5px] uppercase text-carbon-gray-50">{visible.length} record{visible.length === 1 ? '' : 's'}</span>
        <button onClick={exportCsv} disabled={!visible.length} className="flex items-center gap-1 px-2 py-1.5 text-xs border border-carbon-gray-30 text-carbon-gray-80 hover:bg-carbon-gray-10 disabled:opacity-50">
          <Download size={13} /> Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="bg-carbon-white border border-carbon-border overflow-hidden">
        <CpscTable
          className="w-full text-xs"
          headClassName={CPSC_TH_CLASS}
          headRowClassName="text-left"
          columns={[
            { label: '', className: 'px-2 py-2 w-6' },
            { label: 'Report No', className: 'px-2 py-2' },
            { label: 'Style No', className: 'px-2 py-2' },
            { label: 'Version', className: 'px-2 py-2' },
            { label: 'Status', className: 'px-2 py-2' },
            { label: 'Last activity', className: 'px-2 py-2' },
          ]}
        >
            {!visible.length && !loading && (
              <tr><td colSpan={6} className="px-2 py-6 text-center text-carbon-gray-50">No CPSC records in this range.</td></tr>
            )}
            {visible.map((d) => {
              const st = STATUS[d.status] || { tone: 'slate', label: s(d.status) || '—' }; // preserve unknown status, don't mislabel as Draft
              const open = expanded === d.draftId;
              const timeline = auditByDraft[d.draftId];
              return (
                <React.Fragment key={d.draftId}>
                  <tr
                    className="border-t border-carbon-gray-10 hover:bg-carbon-gray-10 cursor-pointer focus:outline-none focus:bg-carbon-gray-10"
                    role="button" tabIndex={0} aria-expanded={open}
                    onClick={() => openTimeline(d.draftId)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openTimeline(d.draftId); } }}
                  >
                    <td className="px-2 py-2 text-carbon-gray-40">{open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</td>
                    <td className="px-2 py-2 font-plex-mono font-medium text-carbon-ink">{s(d.reportNo) || '—'}</td>
                    <td className="px-2 py-2 text-carbon-gray-70">{s(d.styleNo) || '—'}</td>
                    <td className="px-2 py-2 text-carbon-gray-70 font-plex-mono">{s(d.versionId) || '—'}</td>
                    <td className="px-2 py-2"><span className={`inline-flex items-center px-1.5 py-0.5 font-plex-mono text-[10px] uppercase border ${TONE[st.tone]}`}>{st.label}</span></td>
                    <td className="px-2 py-2 font-plex-mono text-carbon-gray-50">{fmt(d.updatedAt)}</td>
                  </tr>
                  {open && (
                    <tr className="bg-carbon-gray-10/60"><td colSpan={6} className="px-3 py-2">
                      {timeline?.loading && <div className="text-carbon-gray-50 flex items-center gap-1 py-2"><Loader2 size={12} className="animate-spin" /> Loading timeline…</div>}
                      {timeline?.error && <div className="text-carbon-red-60 py-2">{timeline.error}</div>}
                      {timeline?.events && !timeline.events.length && <div className="text-carbon-gray-50 py-2">No audit events recorded.</div>}
                      {timeline?.events && timeline.events.length > 0 && (
                        <ol className="relative border-l border-carbon-border ml-2 space-y-2 py-1">
                          {timeline.events.map((e) => {
                            const a = ACTION[e.action] || { icon: Clock, label: e.action };
                            const Icon = a.icon;
                            return (
                              <li key={e.id} className="ml-4">
                                <span className="absolute -left-[7px] mt-0.5 w-3 h-3 rounded-full bg-carbon-white border border-carbon-gray-30 flex items-center justify-center"><Icon size={8} className="text-carbon-gray-70" /></span>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-carbon-gray-80">{a.label}</span>
                                  {e.fromStatus && e.toStatus && <span className="font-plex-mono text-[10px] text-carbon-gray-50">{e.fromStatus} → {e.toStatus}</span>}
                                  <span className="font-plex-mono text-[10px] text-carbon-gray-50">· {fmt(e.at)}</span>
                                  {e.byUid && <span className="font-plex-mono text-[10px] text-carbon-gray-50">· by {s(e.operatorEmail) || s(e.byUid).slice(0, 8)}</span>}
                                </div>
                                {(e.field || e.brokerEmail || e.errorCount != null || e.certificateId || e.doCertify === true) && (
                                  <div className="font-plex-mono text-[10px] text-carbon-gray-50 mt-0.5">
                                    {e.field && <span>field: {e.field} </span>}
                                    {e.brokerEmail && <span>→ broker </span>}
                                    {e.errorCount != null && <span>{e.errorCount} error(s) </span>}
                                    {e.certificateId && <span>cert {s(e.certificateId)} </span>}
                                    {e.doCertify === true && <span className="text-carbon-green-50">doCertify=TRUE </span>}
                                  </div>
                                )}
                                {/* CHA-171: raw CPSC request/response per submission attempt (§2 audit) */}
                                {(e.cpscRequest || e.cpscResponse || e.readback) && (
                                  <details className="mt-1">
                                    <summary className="font-plex-mono text-[10px] text-carbon-gray-50 cursor-pointer select-none hover:text-carbon-gray-70">
                                      CPSC wire data{e.httpStatus != null ? ` · HTTP ${e.httpStatus}` : ''}{e.versionId ? ` · ${s(e.versionId)}` : ''}
                                    </summary>
                                    <div className="space-y-1 mt-1">
                                      {e.cpscRequest && (
                                        <div>
                                          <div className="font-plex-mono text-[10px] font-medium text-carbon-gray-50 uppercase">Request payload</div>
                                          <pre className="text-[10px] font-plex-mono bg-carbon-white border border-carbon-border p-2 max-h-48 overflow-auto whitespace-pre-wrap break-all">{s(e.cpscRequest)}</pre>
                                        </div>
                                      )}
                                      {e.cpscResponse && (
                                        <div>
                                          <div className="font-plex-mono text-[10px] font-medium text-carbon-gray-50 uppercase">CPSC response</div>
                                          <pre className="text-[10px] font-plex-mono bg-carbon-white border border-carbon-border p-2 max-h-48 overflow-auto whitespace-pre-wrap break-all">{s(e.cpscResponse)}</pre>
                                        </div>
                                      )}
                                      {e.readback && (
                                        <div>
                                          <div className="font-plex-mono text-[10px] font-medium text-carbon-gray-50 uppercase">Post-failure read-back</div>
                                          <pre className="text-[10px] font-plex-mono bg-carbon-white border border-carbon-border p-2 max-h-48 overflow-auto whitespace-pre-wrap break-all">{JSON.stringify(e.readback, null, 2)}</pre>
                                        </div>
                                      )}
                                    </div>
                                  </details>
                                )}
                              </li>
                            );
                          })}
                        </ol>
                      )}
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
        </CpscTable>
      </div>

      <p className="text-[11px] text-carbon-gray-50 flex items-center gap-1">
        <History size={11} /> Read-only audit trail. Timeline events are immutable. Filtered on last activity ({DATE_RANGES.find((r) => r.days === rangeDays)?.label.toLowerCase()}).
      </p>
    </div>
  );
}
